import { ChangeEvent, useCallback, useEffect, useState } from "react";
import "../styles/App.css";
import "../styles/Toolbar.css";
import "../styles/Timeline.css";
import "bootstrap/dist/css/bootstrap.min.css";
import NavbarComponent from "./TopNavbar";
import {
  Container,
  Row,
  Col,
  Form,
  Modal,
  Button,
  ToastContainer,
  Toast,
  Tabs,
  Tab,
} from "react-bootstrap";
import RemarksAccordionComponent from "./RemarksAccordionComponent";
import AccordionComponent from "./AccordionComponent";
import DanceFloorVisualization from "./DanceFloorVisualization";
import VerticalToolbar from "./VerticalToolbar";
import BottomNavbar from "./BottomNavbar";
import {
  Choreography,
  ConvexHull,
  Dance,
  DanceCouple,
  DiscreteHeatmap,
  IntermediateDanceCouple,
  IntermediatePattern,
  IntermediatePosition,
  Pattern,
  Pose,
  Position,
} from "../interfaces/Choreography";
import { SelectOption } from "../interfaces/Toolbar";
import { fetchChoreography } from "../services/FileServices";
import TableView from "./TableView";
import DistancesBarChart from "./DistancesBarChart";
import Timeline from "./Timeline";
import { useImmer } from "use-immer";
import {
  clampToGridPosition,
  getBeatDistance,
  findMaxFrequencyInHeatmap,
  findConvexHull,
  isPointSymmetric,
  getStartBarForDanceRemoval,
  getEndBarForDanceRemoval,
  getFirstPatternId,
  getPatternsInDanceAmount,
  lumDiffAlt,
  diffToLighten,
  customLighten,
} from "../services/VisServices";
import DismissibleAlert from "./DismissibleAlert";
import {
  Arrow90degLeft,
  Arrow90degRight,
  Eye,
  Pencil,
  WindowDock,
} from "react-bootstrap-icons";
import View3D from "./3DView";
import PoseView from "./PoseView";
import Legend from "./Legend";
import TimelineDanceDriven from "./TimelineDanceDriven";
import FootModal from "./FootModal";
import OrientationVisModal from "./OrientationVisModal";
import Color from "color";
import { WritableDraft } from "immer/dist/internal";
import { ShortcutMenu } from "./ShortcutMenu";
import { coupleColors } from "../services/CoupleColors";

const DEFAULT_GENTLEMAN_COLOR = "#536FA6";
const DEFAULT_LADY_COLOR = "#9DA65D";
const DEFAULT_COUPLE_COLOR = "#B4674B";

function App(props: { choreoName: string }) {
  const { choreoName } = props;

  const [choreo, setChoreo] = useImmer<Choreography | null>(null);
  const [selectedPattern, setSelectedPattern] = useState(0);
  const [discreteHeatmap, setDiscreteHeatmap] =
    useImmer<DiscreteHeatmap | null>(null);
  const [highestFrequency, setHighestFrequency] = useState(25);

  const [hoveredDancer, setHoveredDancer] = useState<number>(-1);
  const [selectedDancerFor3D, setSelectedDancerFor3D] = useState(0);
  const [selectedHeatmapTile, setSelectedHeatmapTile] = useState({
    x: -9,
    y: -9,
  });

  const [brushCopy, setBrushCopy] = useState<number[]>([]);

  const [copyOfFirstDancer, setCopyOfFirstDancer] = useState<number>(-1);
  const [firstDanceCouple, setFirstDanceCouple] = useState<DanceCouple>();
  const [firstDancer, setFirstDancer] = useState<Position>();

  function updateFirstDancerCopy(newId: number) {
    setCopyOfFirstDancer(newId);

    if (newId > 0) {
      const coupleNumber: number = Math.floor((newId - 1) / 2);
      const dancerNumberInDanceCouple: number = (newId - 1) % 2;

      setFirstDanceCouple(
        choreo?.patterns[selectedPattern].positions[coupleNumber]
      );
      setFirstDancer(
        choreo?.patterns[selectedPattern].positions[coupleNumber].position[
          dancerNumberInDanceCouple
        ]
      );
    }
  }

  const [brushRotationAngle, setBrushRotationAngle] = useState<number>(0);

  const [formScalingFactor, setFormScalingFactor] = useState<[number, number]>([
    0.5, 0.5,
  ]);

  const [selectOptions, setSelectOptions] = useState<SelectOption[]>([
    { value: -1, label: "", key: "-1" },
  ]);

  const [showGentlemanInCouple, setShowGentlemanInCouple] =
    useState<boolean>(true);
  function switchShowGentlemanInCouple(e: any) {
    if (showGentlemanInCouple) {
      setShowGentlemanInCouple(false);
    } else {
      setShowGentlemanInCouple(true);
    }
  }

  //User selected icon colors
  const [gentlemanColor, setGentlemanColor] = useState<
    string | number | readonly string[] | undefined
  >(DEFAULT_GENTLEMAN_COLOR);
  const [ladyColor, setLadyColor] = useState<
    string | number | readonly string[] | undefined
  >(DEFAULT_LADY_COLOR);
  const [coupleColor, setCoupleColor] = useState<
    string | number | readonly string[] | undefined
  >(DEFAULT_COUPLE_COLOR);

  //Color scheme for the transition (user selectable in the settings)
  const [transitionColorScheme, setTransitionColorScheme] =
    useState<string>("RdYlBu");

  const [animationSpeed, setAnimationSpeed] = useState(1);

  //Wheter the modal indicating a mode switch should be displayed
  const [show, setShow] = useState(false);

  //Show orientation vis modal
  const [showOrientationModal, setShowOrientationModal] =
    useState<boolean>(false);

  //Show foot modal
  const [showFootModal, setShowFootModal] = useState<boolean>(false);

  //Wheter pattern information can be changed by the user
  const [editingModeActive, setEditingModeActive] = useState(false);

  //Pattern rotation inside the main visualization
  const [patternRotation, setPatternRotation] = useState<
    "Front" | "Back" | "Left" | "Right"
  >("Front");

  const switchModeModalShowTime = 1000;
  useEffect(() => {
    setShow(true);
    setTimeout(function () {
      setShow(false);
    }, switchModeModalShowTime);
  }, [editingModeActive, setEditingModeActive]);

  //Wheter to show the grid fine (0.5 metres) or coarse (1.0 metres)
  const [fineGridEnabled, setFineGridEnabled] = useState(true);

  //Whether to show the coarse grid (1.0 metres) as black lines in the 3D vis
  const [showGridIn3D, setShowGridIn3D] = useState(true);

  //Wheter the discrete or continous heatmap gets shown in the analysis mode
  const [discreteHeatmapEnabled, setDiscreteHeatmapEnabled] = useState(true);

  //Wheter the drag and drop snaps dancer icons to 0.5m-grid positions while moving the icon
  const [gridDragEnabled, setGridDragEnabled] = useState(true);

  //Mode selection variables
  const [show3DView, setShow3DView] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showShapes, setShowShapes] = useState(false);
  const [showTransitions, setShowTransitions] = useState(false);
  const [showOrientations, setShowOrientations] = useState(false);
  const [showTangentBodyParts, setShowTangentBodyParts] = useState(false);

  //Wheter slider is shown with which the breaks between transitions in an animation can
  //be changed
  const [showSliderForAnimatedChoreo, setShowSliderForAnimatedChoreo] =
    useState(false);
  const [speedForAnimatedChoreo, setSpeedForAnimatedChoreo] = useState(1);
  const [currentlyInAnimation, setCurrentlyInAnimation] = useState(false);

  //Go back to the normal mode (or stay in 3D-mode) if an animation is playing
  useEffect(() => {
    if (currentlyInAnimation) {
      setShowOrientations(false);
      setShowTransitions(false);
      setShowShapes(false);
    }
  }, [currentlyInAnimation]);

  //Wheter alerts should be shown (invalid movement on the timeline, invalid rotation of lines of dancers, etc.)
  const [showLongMovementAlert, setShowLongMovementAlert] = useState(false);
  const [showPatternCollisionAlert, setShowPatternCollisionAlert] =
    useState(false);
  const [showScalingAlert, setShowScalingAlert] = useState(false);
  const [showNewPatternAlert, setShowNewPatternAlert] = useState(false);
  const [showRemoveLastDanceAlert, setShowRemoveLastDanceAlert] =
    useState(false);
  const [showRemoveLastPatternAlert, setShowRemoveLastPatternAlert] =
    useState(false);

  const [highestBarNumber, setHighestBarNumber] = useState(60);
  const [timelineMapValues, setTimelineMapValues] = useState<number[]>([]);

  function frontsideColorOrientationModal() {
    const coupleNumber: number = Math.floor((copyOfFirstDancer - 1) / 2);
    const dancerNumberInDanceCouple: number = (copyOfFirstDancer - 1) % 2;

    const couple = choreo!.patterns[selectedPattern].positions[coupleNumber];
    const dancer = couple!.position[dancerNumberInDanceCouple];
    if (choreo?.colorCodedCouples) {
      return choreo.coupleColors[coupleNumber + 1].fillColor;
    } else {
      if (couple.joint) return String(coupleColor);
      if (dancerNumberInDanceCouple === 0) {
        return String(gentlemanColor);
      } else {
        return String(ladyColor);
      }
    }
    return String(gentlemanColor);
  }

  function backsideColorOrientationModal() {
    const frontsideColor = frontsideColorOrientationModal();
    const diff = lumDiffAlt(Color("#FFFFFF"), Color(frontsideColor));
    return String(customLighten(Color(frontsideColor), diffToLighten(diff)));
  }

  //Change the name of the choreography
  const setChoreoName = useCallback(
    (newChoreoName: string) => {
      setChoreo((draft) => {
        draft!.name = newChoreoName;
      });
    },
    [setChoreo]
  );

  //Change the name of a single pattern
  const setPatternName = useCallback(
    (patternId: number, newPatternName: string) => {
      setChoreo((draft) => {
        draft!.patterns[patternId].name = newPatternName;
      });
    },
    [setChoreo]
  );

  //Set usage of the use of manual color specification for each couple
  const setColorCodedCouples = useCallback(
    (newColorCodedCouples: boolean) => {
      setChoreo((draft) => {
        draft!.colorCodedCouples = newColorCodedCouples;
      });
    },
    [setChoreo]
  );

  //Change the couple color record
  const setCoupleColors = useCallback(
    (newCoupleColors: Record<number, { fillColor: string }>) => {
      setChoreo((draft) => {
        draft!.coupleColors = newCoupleColors;
      });
    },
    [setChoreo]
  );

  /**
   * Takes all dancers that are selected by the brush and rotate them around the dancer with the id firstDancer
   * (this dancer has a special green border). The dancers positions are updated accordingly and always lie
   * within the dance floor.
   */
  const rotateBrush = useCallback(
    (
      patternId: number,
      brush: number[],
      firstDancer: number,
      rotationAngle: number
    ) => {
      setChoreo((draft) => {
        const pattern = draft!.patterns[patternId];
        const rotationCenter =
          pattern.positions[Math.floor((firstDancer - 1) / 2)].position[
            (firstDancer - 1) % 2
          ];

        brush.forEach((id) => {
          const dancerPos =
            pattern.positions[Math.floor((id - 1) / 2)].position[(id - 1) % 2];

          const angleRad = (rotationAngle * Math.PI) / 180;

          const centeredPos: [number, number] = [
            dancerPos.x - rotationCenter.x,
            dancerPos.y - rotationCenter.y,
          ];
          let rotatedPos: [number, number] = [
            centeredPos[0] * Math.cos(angleRad) -
              centeredPos[1] * Math.sin(angleRad),
            centeredPos[0] * Math.sin(angleRad) +
              centeredPos[1] * Math.cos(angleRad),
          ];

          let resultPos: [number, number] = [
            clampToGridPosition(rotatedPos[0] + rotationCenter.x, -8, 8),
            clampToGridPosition(rotatedPos[1] + rotationCenter.y, -8, 8),
          ];

          dancerPos.x = resultPos[0];
          dancerPos.y = resultPos[1];
        });
      });
    },
    [setChoreo]
  );

  const scaleBrush = useCallback(
    (patternId: number, brush: number[], scalingFactor: [number, number]) => {
      setChoreo((draft) => {
        const pattern = draft!.patterns[patternId];

        //Calculate the center position of all selected dancers
        let midPos: [number, number] = [0, 0];
        brush.forEach((id) => {
          const dancerPos =
            pattern.positions[Math.floor((id - 1) / 2)].position[(id - 1) % 2];
          midPos[0] += dancerPos.x;
          midPos[1] += dancerPos.y;
        });
        midPos[0] /= brush.length;
        midPos[1] /= brush.length;

        if (isPointSymmetric(pattern.positions, brush, midPos)) {
          midPos[0] = clampToGridPosition(midPos[0], -8, 8);
          midPos[1] = clampToGridPosition(midPos[1], -8, 8);

          //Iterate over all selected dancers
          brush.forEach((id) => {
            const dancerPos =
              pattern.positions[Math.floor((id - 1) / 2)].position[
                (id - 1) % 2
              ];

            //If the dancer is on the right hand side of the center point, than this relation should be preserved (also for the left hand side)
            if (dancerPos.x > midPos[0]) {
              dancerPos.x = clampToGridPosition(
                midPos[0] + scalingFactor[0],
                -8,
                8
              );
            } else if (dancerPos.x < midPos[0]) {
              dancerPos.x = clampToGridPosition(
                midPos[0] - scalingFactor[0],
                -8,
                8
              );
            }

            //Analogue for up/down
            if (dancerPos.y > midPos[1]) {
              dancerPos.y = clampToGridPosition(
                midPos[1] + scalingFactor[1],
                -8,
                8
              );
            } else if (dancerPos.y < midPos[1]) {
              dancerPos.y = clampToGridPosition(
                midPos[1] - scalingFactor[1],
                -8,
                8
              );
            }
          });
        } else {
          setShowScalingAlert(true);
        }
      });
    },
    [setChoreo]
  );

  //Change the description of the choreography
  const setChoreoDescription = useCallback(
    (newChoreoDescription: string) => {
      setChoreo((draft) => {
        draft!.description = newChoreoDescription;
      });
    },
    [setChoreo]
  );

  //Set a new remark string (replaces the old one)
  const setRemarks = useCallback(
    (patternId: number, newRemarks: string) => {
      setChoreo((draft) => {
        const todo = draft?.patterns[patternId];
        todo!.remarks = newRemarks;
      });
    },
    [setChoreo]
  );

  /**
   * Moves a pattern to a new bar and beat. The callback checks if the movement is allowed
   * and if not, shows an alert message.
   */
  const setBarAndBeat = useCallback(
    (
      patternId: number,
      newBar: number,
      newBeat: number,
      highestBarNumber: number
    ) => {
      setChoreo((draft) => {
        let longMovement: boolean = false;

        const pattern = draft!.patterns[patternId];
        if (patternId > 0) {
          let previousPattern: Pattern | IntermediatePattern =
            draft!.patterns[patternId - 1];
          if (draft!.patterns[patternId].intermediatePatterns.length > 0) {
            const lastIntermediatePatternId: number =
              draft!.patterns[patternId].intermediatePatterns.length - 1;
            previousPattern =
              draft!.patterns[patternId].intermediatePatterns[
                lastIntermediatePatternId
              ];
          }
          if (
            previousPattern.bar > newBar ||
            (previousPattern.bar === newBar && previousPattern.beat >= newBeat)
          ) {
            longMovement = true;
          }
        }

        if (patternId < draft!.patterns.length - 1) {
          let followingPattern: Pattern | IntermediatePattern =
            draft!.patterns[patternId + 1];
          if (draft!.patterns[patternId + 1].intermediatePatterns.length > 0) {
            followingPattern =
              draft!.patterns[patternId + 1].intermediatePatterns[0];
          }
          if (
            followingPattern.bar < newBar ||
            (followingPattern.bar === newBar &&
              followingPattern.beat <= newBeat)
          ) {
            longMovement = true;
          }
        }

        if (newBar >= highestBarNumber) {
          longMovement = true;
        }

        //Check that you cannot move a pattern onto another
        for (let i = 0; i < draft!.patterns.length; i++) {
          if (i !== patternId) {
            const comparisonPattern = draft!.patterns[i];
            if (
              comparisonPattern.bar === newBar &&
              comparisonPattern.beat === newBeat
            ) {
              setShowPatternCollisionAlert(true);
              return;
            }
          }
        }

        pattern.bar = newBar;
        pattern.beat = newBeat;

        if (longMovement) {
          setShowLongMovementAlert(true);

          //Remove all intermediate patterns from the current pattern
          pattern.intermediatePatterns = [];

          //If a pattern after the current exists (BEFORE the movement), remove all its intermediate patterns

          if (patternId + 1 < draft!.patterns.length) {
            const nextPattern = draft!.patterns[patternId + 1];
            nextPattern.intermediatePatterns = [];
          }

          //Remove pattern from array
          draft!.patterns.splice(patternId, 1);

          //Search the new position in the array
          let searchIndex = 0;
          for (let i = 0; i < draft!.patterns.length; i++) {
            const comparisonPattern = draft!.patterns[i];
            if (
              pattern.bar > comparisonPattern.bar ||
              (pattern.bar === comparisonPattern.bar &&
                pattern.beat > comparisonPattern.beat)
            ) {
              searchIndex = i;
            }
          }

          //Add the moved pattern
          draft!.patterns.splice(searchIndex + 1, 0, pattern);

          //Update the indices
          let index = 0;
          draft!.patterns.forEach((currentPattern) => {
            currentPattern.id = index;
            index++;
          });

          //If a pattern after the current exists (AFTER the movement), remove all its intermediate patterns
          if (searchIndex + 1 < draft!.patterns.length) {
            const nextPattern = draft!.patterns[searchIndex + 2];
            nextPattern.intermediatePatterns = [];
          }

          setSelectedPattern((draft) => (draft = searchIndex + 1));
        }
      });
    },
    [setChoreo, setSelectedPattern]
  );

  const addPatternToDraft = useCallback(
    (patternNameInDraft: string, pattern: Pattern) => {
      setChoreo((draft) => {
        let nameAlreadyInDraft = false;
        draft!.draft.forEach((currentPattern) => {
          if (currentPattern.name === patternNameInDraft) {
            nameAlreadyInDraft = true;
          }
        });
        if (!nameAlreadyInDraft) {
          draft!.draft.push(pattern);
        }
      });
    },
    [setChoreo]
  );

  const addNewDance = useCallback(
    (name: string, bars: number, beatsPerBar: number) => {
      setChoreo((draft) => {
        const newDance: Dance = {
          name: name,
          bars: bars,
          beatsPerBar: beatsPerBar,
        };
        draft?.dances.push(newDance);
      });
    },
    [setChoreo]
  );

  const promoteIntermediatePattern = useCallback(
    (bar: number, beat: number) => {
      setChoreo((draft) => {
        //Search for the intermediate pattern (and associated pattern) at the given beat and bar
        let patternId = -1;
        let intermediatePatternId = -1;

        let found: boolean = false;

        for (let i = 0; i < draft!.patterns.length; i++) {
          const pattern = draft!.patterns[i];
          for (let j = 0; j < pattern.intermediatePatterns.length; j++) {
            const intermediatePattern = pattern.intermediatePatterns[j];

            if (
              intermediatePattern.beat === beat &&
              intermediatePattern.bar === bar
            ) {
              patternId = i;
              intermediatePatternId = j;
              found = true;
              break;
            }
          }

          if (found) break;
        }

        //If no intermediate pattern found at the given beat and bar, exit the function
        if (patternId === -1 || intermediatePatternId === -1) return;

        const nextPattern = draft!.patterns[patternId];
        const patternToPromote =
          nextPattern.intermediatePatterns[intermediatePatternId];
        //Create a new pattern object and copy information from the next pattern (split the intermediate patterns accordingly)
        const newDanceCouples: DanceCouple[] = [];

        patternToPromote.positions.forEach((intermediateCouple) => {
          const manInNextPattern =
            nextPattern.positions[intermediateCouple.id - 1].position[0];
          const womanInNextPattern =
            nextPattern.positions[intermediateCouple.id - 1].position[1];

          const man: Position = {
            x: intermediateCouple.position[0].x,
            y: intermediateCouple.position[1].y,
            bodyOrientation: manInNextPattern.bodyOrientation,
            headOrientation: manInNextPattern.headOrientation,
            pose: manInNextPattern.pose,
            tangentBodyPart: manInNextPattern.tangentBodyPart,
          };

          const woman: Position = {
            x: intermediateCouple.position[0].x,
            y: intermediateCouple.position[1].y,
            bodyOrientation: womanInNextPattern.bodyOrientation,
            headOrientation: womanInNextPattern.headOrientation,
            pose: womanInNextPattern.pose,
            tangentBodyPart: womanInNextPattern.tangentBodyPart,
          };

          const newCouple: DanceCouple = {
            id: intermediateCouple.id,
            joint: intermediateCouple.joint,
            position: [man, woman],
          };
          newDanceCouples.push(newCouple);
        });

        let leftPatternSet: IntermediatePattern[] = [];
        let rightPatternSet: IntermediatePattern[] = [];

        const remainingPatternAmount =
          nextPattern.intermediatePatterns.length - intermediatePatternId - 1;

        if (intermediatePatternId > 0)
          leftPatternSet = nextPattern.intermediatePatterns.slice(
            0,
            intermediatePatternId
          );
        if (remainingPatternAmount > 0)
          rightPatternSet = nextPattern.intermediatePatterns.slice(
            -remainingPatternAmount
          );

        for (let i = 0; i < leftPatternSet.length; i++) {
          leftPatternSet[i].id = i;
        }

        for (let i = 0; i < rightPatternSet.length; i++) {
          rightPatternSet[i].id = i;
        }

        nextPattern.intermediatePatterns = [];
        nextPattern.intermediatePatterns = rightPatternSet;

        const promotedPattern: Pattern = {
          id: patternId,
          name: "Promoted pattern",
          remarks: "",
          pointDescription: "",
          bar: bar,
          beat: beat,
          shapes: [],
          positions: newDanceCouples,
          intermediatePatterns: leftPatternSet,
        };

        for (let i = patternId; i < draft!.patterns.length; i++) {
          draft!.patterns[i].id += 1;
        }

        draft!.patterns.splice(promotedPattern.id, 0, promotedPattern);
      });
    },
    [setChoreo]
  );

  const updateDanceName = useCallback(
    (danceId: number, newName: string) => {
      setChoreo((draft) => {
        draft!.dances[danceId].name = newName;
      });
    },
    [setChoreo]
  );

  useEffect(() => {
    console.log(choreo);
  }, [choreo]);

  const removeBar = useCallback(
    (barToRemove: number) => {
      setChoreo((draft) => {
        //Find the dance the bar is in
        let dance: Dance = draft!.dances[0];
        let barCounter = 0;
        for (let i = 0; i < draft!.dances.length; i++) {
          const currentDance = draft!.dances[i];
          barCounter += currentDance.bars;
          if (barToRemove < barCounter) {
            dance = currentDance;
            break;
          }
        }

        //Interrupt if the dance only contains a single bar (this bar must remain)
        if (dance.bars <= 1) return;

        //Remove bar from dance if there is remaining bar in dance
        dance.bars -= 1;

        let patternIdsToRemove: number[] = [];
        draft!.patterns.forEach((pattern) => {
          if (pattern.bar === barToRemove) {
            patternIdsToRemove.push(pattern.id);
          }
        });

        draft!.patterns.forEach((pattern) => {
          if (pattern.bar > barToRemove) pattern.bar--;
        });

        //Search for all patterns in the bar to remove and also remove them
        patternIdsToRemove.forEach((id) => {
          if (id < draft!.patterns.length - 1) {
            draft!.patterns[id + 1].intermediatePatterns = [];
          }

          draft!.patterns.splice(id, 1);

          for (let i = 0; i < draft!.patterns.length; i++) {
            draft!.patterns[i].id = i;
          }
        });

        //Update heatmap data
        initializeDiscreteHeatmap(draft!);
      });
    },
    [setChoreo]
  );

  const removePattern = useCallback(
    (patternIdToRemove: number) => {
      setChoreo((draft) => {
        if (patternIdToRemove < draft!.patterns.length - 1) {
          draft!.patterns[patternIdToRemove + 1].intermediatePatterns = [];
        }

        updateDiscreteHeatmapAfterPatternDeletion(
          draft!.patterns[patternIdToRemove]
        );
        draft!.patterns.splice(patternIdToRemove, 1);

        for (let i = 0; i < draft!.patterns.length; i++) {
          draft!.patterns[i].id = i;
        }
      });
      setSelectedPattern(
        (draft) => (draft = Math.max(0, patternIdToRemove - 1))
      );
    },
    [setChoreo, setSelectedPattern]
  );

  const removeDance = useCallback(
    (danceId: number) => {
      setChoreo((draft) => {
        //Check if the the choreo only has 1 dance left: If yes, the user is not allowed to delete the dance
        if (draft!.dances.length <= 1) {
          setShowRemoveLastDanceAlert(true);
          return;
        }

        //Find the start (in bars, inclusive) and the end (in bars, inclusive) of the dance to remove
        let startInBars: number = getStartBarForDanceRemoval(draft!, danceId);
        let endInBars: number = getEndBarForDanceRemoval(draft!, danceId);

        const barsToRemove = endInBars - startInBars + 1;

        draft!.patterns.forEach((pattern) => {
          if (pattern.bar >= startInBars && pattern.bar <= endInBars) {
            if (pattern.bar > endInBars) pattern.bar -= barsToRemove;
          }
        });

        //Remove all patterns contained in the dance (this also removes intermediate patterns)
        let firstPatternId: number = getFirstPatternId(
          draft!,
          startInBars,
          endInBars
        );
        let patternsInDanceAmount: number = getPatternsInDanceAmount(
          draft!,
          startInBars,
          endInBars
        );

        if (patternsInDanceAmount === draft!.patterns.length) {
          setShowRemoveLastPatternAlert(true);
          return;
        }

        const patternIdToRemove = firstPatternId;
        if (firstPatternId > -1 && patternsInDanceAmount > 0) {
          for (let i = 0; i < patternsInDanceAmount; i++) {
            //Remove intermediate patterns of the following pattern (between pattern to remove and following pattern)
            if (patternIdToRemove < draft!.patterns.length - 1) {
              draft!.patterns[patternIdToRemove + 1].intermediatePatterns = [];
            }

            //Remove the pattern
            draft!.patterns.splice(patternIdToRemove, 1);

            //Shift ids of consequent patterns
            for (let i = 0; i < draft!.patterns.length; i++) {
              draft!.patterns[i].id = i;
            }
          }
        }

        //Shift consequent patterns bar mark
        if (firstPatternId > -1) {
          for (let i = firstPatternId; i < draft!.patterns.length; i++) {
            draft!.patterns[i].bar -= barsToRemove;
          }
        }

        //Remove the dance
        draft!.dances.splice(danceId, 1);

        //Update heatmap data
        initializeDiscreteHeatmap(draft!);
      });
    },
    [setChoreo]
  );

  function addNewBar() {
    const newHighestBarNumber = highestBarNumber + 1;
    const localTimelineMapValues: number[] = [];
    for (let i = 0; i <= newHighestBarNumber; i++) {
      localTimelineMapValues.push(i);
    }
    setTimelineMapValues(localTimelineMapValues);
    setHighestBarNumber(newHighestBarNumber);
  }

  const addPattern = useCallback(
    (newPattern: Pattern) => {
      setChoreo((draft) => {
        //Check if the new patterns bar and beat already contain another pattern
        let collision = false;
        draft!.patterns.forEach((pattern) => {
          if (
            pattern.bar === newPattern.bar &&
            pattern.beat === newPattern.beat
          ) {
            collision = true;
          }
        });
        if (collision) {
          setShowPatternCollisionAlert(true);
          return;
        }

        let newId = 0;
        //Determine pattern id
        draft!.patterns.forEach((pattern) => {
          if (
            pattern.bar < newPattern.bar ||
            (pattern.bar === newPattern.bar && pattern.beat < newPattern.beat)
          ) {
            newId++;
          }
        });

        newPattern.id = newId;

        //Shift all consequent pattern ids by 1
        for (let i = newId; i < draft!.patterns.length; i++) {
          draft!.patterns[i].id++;
        }

        draft!.patterns.splice(newId, 0, newPattern);
        setSelectedPattern(newId);
        updateDiscreteHeatmapAfterPatternInsertion(newPattern);
      });
    },
    [setChoreo]
  );

  useEffect(() => {
    if (choreo && choreo !== null) {
      if (
        choreo!.patterns[choreo!.patterns.length - 1].bar > highestBarNumber
      ) {
        const newHighestBarNumber = highestBarNumber + 1;
        const localTimelineMapValues: number[] = [];
        for (let i = 0; i <= newHighestBarNumber; i++) {
          localTimelineMapValues.push(i);
        }
        setTimelineMapValues(localTimelineMapValues);
        setHighestBarNumber(newHighestBarNumber);
      }
    }
  }, [choreo]);

  //Remove all intermediate patterns contained in the time interval between the last and current pattern
  const removeAllIntermediatePatterns = useCallback(
    (patternId: number) => {
      setChoreo((draft) => {
        draft!.patterns[patternId].intermediatePatterns = [];
      });
    },
    [setChoreo]
  );

  const moveDancerInIntermediatePattern = useCallback(
    (
      patternId: number,
      intermediatePatternId: number,
      dancerId: number,
      newX: number,
      newY: number
    ) => {
      setChoreo((draft) => {
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        const partnerNumberInDanceCouple: number =
          dancerNumberInDanceCouple === 1 ? 0 : 1;

        let danceCouple: IntermediateDanceCouple =
          draft!.patterns[patternId].intermediatePatterns[intermediatePatternId]
            .positions[coupleNumber];

        let positionToChange: IntermediatePosition =
          danceCouple.position[dancerNumberInDanceCouple];

        positionToChange.x = newX;
        positionToChange.y = newY;

        if (danceCouple.joint) {
          const partnerPositionToChange: IntermediatePosition =
            danceCouple.position[partnerNumberInDanceCouple];
          partnerPositionToChange.x = newX;
          partnerPositionToChange.y = newY;
        }
      });
    },
    [setChoreo]
  );

  const addIntermediatePattern = useCallback(
    (bar: number, beat: number) => {
      setChoreo((draft) => {
        //Check if the beat and bar are valid (with a possible transition)
        const firstPattern: Pattern = draft!.patterns[0];
        const lastPattern: Pattern =
          draft!.patterns[draft!.patterns.length - 1];

        if (
          bar < firstPattern.bar ||
          (bar === firstPattern.bar && beat <= firstPattern.beat)
        ) {
          return;
        }

        if (
          bar > lastPattern.bar ||
          (bar === lastPattern.bar && beat >= lastPattern.beat)
        ) {
          return;
        }

        let positionToInsert = 0;
        let endIteration = false;
        //Find out pattern or intermediate pattern directly before the insertion point
        let patternBefore: Pattern | IntermediatePattern = draft!.patterns[0];
        draft!.patterns.forEach((pattern) => {
          if (endIteration) return;
          positionToInsert = 0;

          for (let j = 0; j < pattern.intermediatePatterns.length; j++) {
            const intermediatePattern = pattern.intermediatePatterns[j];
            if (
              bar > intermediatePattern.bar ||
              (bar === intermediatePattern.bar &&
                beat > intermediatePattern.beat)
            ) {
              patternBefore = intermediatePattern;
              positionToInsert++;
            } else {
              endIteration = true;
              return;
            }
          }

          if (
            bar > pattern.bar ||
            (bar === pattern.bar && beat > pattern.beat)
          ) {
            patternBefore = pattern;
          } else {
            endIteration = true;
            return;
          }
        });

        //Find out pattern or intermediate pattern directly after the insertion point
        let patternAfter: Pattern | IntermediatePattern = draft!.patterns[0];
        for (let i = 0; i < draft!.patterns.length; i++) {
          const pattern = draft!.patterns[i];
          let found = false;
          for (let j = 0; j < pattern.intermediatePatterns.length; j++) {
            const intermediatePattern = pattern.intermediatePatterns[j];
            if (
              bar < intermediatePattern.bar ||
              (bar === intermediatePattern.bar &&
                beat < intermediatePattern.beat)
            ) {
              patternAfter = intermediatePattern;
              found = true;
              break;
            }
          }
          if (found) break;

          if (
            bar < pattern.bar ||
            (bar === pattern.bar && beat < pattern.beat)
          ) {
            patternAfter = pattern;
            break;
          }
        }

        //TODO: Find the next "real" pattern
        let nextRealPattern = draft!.patterns[draft!.patterns.length - 1];
        for (let i = 0; i < draft!.patterns.length; i++) {
          const pattern = draft!.patterns[i];
          if (
            pattern.bar > bar ||
            (pattern.bar === bar && pattern.beat > beat)
          ) {
            nextRealPattern = pattern;
            break;
          }
        }

        //TODO: Check if there is already a intermediate pattern at that place
        let collision = false;
        nextRealPattern.intermediatePatterns.forEach(
          (exisitingIntermediatePattern) => {
            if (
              exisitingIntermediatePattern.beat === beat &&
              exisitingIntermediatePattern.bar === bar
            ) {
              collision = true;
              return;
            }
          }
        );
        if (collision) return;

        let newIntermediatePattern: IntermediatePattern = {
          id: positionToInsert,
          bar: bar,
          beat: beat,
          positions: [],
        };

        const beatDistanceToPatternBefore = getBeatDistance(
          patternBefore,
          newIntermediatePattern
        );
        const beatDistanceToPatternAfter = getBeatDistance(
          newIntermediatePattern,
          patternAfter
        );

        const relativeDistance =
          beatDistanceToPatternBefore /
          (beatDistanceToPatternBefore + beatDistanceToPatternAfter);

        for (let coupleId = 1; coupleId <= draft!.numCouples; coupleId++) {
          const gentlemanPosBefore =
            patternBefore.positions[coupleId - 1].position[0];
          const ladyPosBefore =
            patternBefore.positions[coupleId - 1].position[1];

          const gentlemanPosAfter =
            patternAfter.positions[coupleId - 1].position[0];
          const ladyPosAfter = patternAfter.positions[coupleId - 1].position[1];

          const gentlemanIntermediatePos: IntermediatePosition = {
            x: clampToGridPosition(
              gentlemanPosBefore.x +
                relativeDistance * (gentlemanPosAfter.x - gentlemanPosBefore.x),
              -8,
              8
            ),
            y: clampToGridPosition(
              gentlemanPosBefore.y +
                relativeDistance * (gentlemanPosAfter.y - gentlemanPosBefore.y),
              -8,
              8
            ),
          };
          const ladyIntermediatePos: IntermediatePosition = {
            x: clampToGridPosition(
              ladyPosBefore.x +
                relativeDistance * (ladyPosAfter.x - ladyPosBefore.x),
              -8,
              8
            ),
            y: clampToGridPosition(
              ladyPosBefore.y +
                relativeDistance * (ladyPosAfter.y - ladyPosBefore.y),
              -8,
              8
            ),
          };

          const intermediateCouple: IntermediateDanceCouple = {
            id: coupleId,
            joint: patternBefore.positions[coupleId - 1].joint,
            position: [gentlemanIntermediatePos, ladyIntermediatePos],
          };

          newIntermediatePattern.positions.push(intermediateCouple);
        }
        //Find out pattern to insert the intermediate position
        let patternToInsert: Pattern = draft!.patterns[0];
        for (let i = 0; i < draft!.patterns.length; i++) {
          const pattern = draft!.patterns[i];
          if (
            bar < pattern.bar ||
            (bar === pattern.bar && beat < pattern.beat)
          ) {
            patternToInsert = pattern;
            break;
          }
        }

        patternToInsert.intermediatePatterns.forEach((intermediatePattern) => {
          if (intermediatePattern.id >= newIntermediatePattern.id) {
            intermediatePattern.id++;
          }
        });

        patternToInsert.intermediatePatterns.push(newIntermediatePattern);

        patternToInsert.intermediatePatterns.sort((a, b) => {
          if (a.id < b.id) {
            return -1;
          } else {
            return 1;
          }
        });
      });
    },
    [setChoreo]
  );

  const moveDancerTogetherWithPartner = useCallback(
    (patternId: number, dancerId: number, newX: number, newY: number) => {
      let calledHeatmapUpdate = false;
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        const partnerNumberInCouple: number =
          dancerNumberInDanceCouple === 0 ? 1 : 0;
        const dancerPosition: Position =
          pattern!.positions[coupleNumber].position[dancerNumberInDanceCouple];
        const partnerPosition: Position =
          pattern!.positions[coupleNumber].position[partnerNumberInCouple];

        const discreteNewX = clampToGridPosition(newX, -8, 8);
        const discreteNewY = clampToGridPosition(newY, -8, 8);
        if (!calledHeatmapUpdate) {
          updateDiscreteHeatmap(
            dancerPosition.x,
            dancerPosition.y,
            discreteNewX,
            discreteNewY
          );
        }
        calledHeatmapUpdate = true;

        dancerPosition.x = discreteNewX;
        dancerPosition.y = discreteNewY;
        partnerPosition.x = discreteNewX;
        partnerPosition.y = discreteNewY;
      });
    },
    [setChoreo]
  );

  function moveDancer(
    patternId: number,
    dancerId: number,
    newX: number,
    newY: number
  ) {
    const coupleNumber: number = Math.floor((dancerId - 1) / 2);
    if (choreo!.patterns[patternId].positions[coupleNumber].joint) {
      moveDancerTogetherWithPartner(patternId, dancerId, newX, newY);
    } else {
      moveDancerAlone(patternId, dancerId, newX, newY);
    }
    updateShapes(patternId, dancerId);
  }

  const updateShapes = useCallback(
    (patternId: number, dancerId: number) => {
      setChoreo((draft) => {
        const pattern = draft!.patterns[patternId];
        let dancerSetsToUpdate: number[][] = [];
        pattern.shapes.forEach((shape) => {
          if (shape.brushSelectedDancers.includes(dancerId)) {
            dancerSetsToUpdate.push(shape.brushSelectedDancers);
          }
        });
        pattern.shapes.filter(
          (shape) => !shape.brushSelectedDancers.includes(dancerId)
        );
        dancerSetsToUpdate.forEach((set) => {
          const newConvexHull = findConvexHull(
            pattern.positions,
            set
          ) as ConvexHull;
          pattern.shapes.push(newConvexHull);
        });
      });
    },
    [setChoreo]
  );

  const separateDanceCouple = useCallback(
    (patternId: number, dancerId: number) => {
      setChoreo((draft) => {
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const danceCouple: DanceCouple =
          draft!.patterns[patternId].positions[coupleNumber];
        danceCouple.joint = false;
        updateHeatmapAfterSeparation(
          danceCouple.position[0].x,
          danceCouple.position[0].y
        );

        danceCouple.position[0].bodyOrientation = 0;
        danceCouple.position[0].headOrientation = 0;
        danceCouple.position[1].bodyOrientation = 0;
        danceCouple.position[1].headOrientation = 0;

        danceCouple.position[0].tangentBodyPart = "Body Center";
        danceCouple.position[1].tangentBodyPart = "Body Center";
      });
    },
    [setChoreo]
  );

  const joinDanceCouple = useCallback(
    (patternId: number, dancerId: number) => {
      setChoreo((draft) => {
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        const partnerNumberInCouple: number =
          dancerNumberInDanceCouple === 0 ? 1 : 0;

        const danceCouple: DanceCouple =
          draft!.patterns[patternId].positions[coupleNumber];

        updateHeatmapAfterJoin(
          danceCouple.position[partnerNumberInCouple].x,
          danceCouple.position[partnerNumberInCouple].y
        );

        danceCouple.joint = true;
        danceCouple.position[partnerNumberInCouple].x =
          danceCouple.position[dancerNumberInDanceCouple].x;
        danceCouple.position[partnerNumberInCouple].y =
          danceCouple.position[dancerNumberInDanceCouple].y;

        danceCouple.position[0].bodyOrientation = 0;
        danceCouple.position[1].bodyOrientation = 180;
        danceCouple.position[0].headOrientation = 0;
        danceCouple.position[1].headOrientation = 180;

        danceCouple.position[0].tangentBodyPart = "Couple Center";
        danceCouple.position[1].tangentBodyPart = "Couple Center";
      });
    },
    [setChoreo]
  );

  const moveDancerAlone = useCallback(
    (patternId: number, dancerId: number, newX: number, newY: number) => {
      let calledHeatmapUpdate = false;
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        const dancerPosition: Position =
          pattern!.positions[coupleNumber].position[dancerNumberInDanceCouple];

        const discreteNewX = clampToGridPosition(newX, -8, 8);
        const discreteNewY = clampToGridPosition(newY, -8, 8);
        if (!calledHeatmapUpdate) {
          updateDiscreteHeatmap(
            dancerPosition.x,
            dancerPosition.y,
            discreteNewX,
            discreteNewY
          );
        }

        calledHeatmapUpdate = true;

        dancerPosition.x = discreteNewX;
        dancerPosition.y = discreteNewY;
      });
    },
    [setChoreo]
  );

  const updateHeatmapAfterJoin = useCallback(
    (oldX: number, oldY: number) => {
      setDiscreteHeatmap((draft) => {
        const oldXIndex = oldX * 2 + 16;
        const oldYIndex = oldY * 2 + 16;

        draft!.values[oldXIndex][oldYIndex] -= 1;
      });
    },
    [setDiscreteHeatmap, setHighestFrequency]
  );

  useEffect(() => {
    if (discreteHeatmap) {
      setHighestFrequency(findMaxFrequencyInHeatmap(discreteHeatmap!));
    }
  }, [discreteHeatmap]);

  const updateHeatmapAfterSeparation = useCallback(
    (x: number, y: number) => {
      setDiscreteHeatmap((draft) => {
        const xIndex = x * 2 + 16;
        const yIndex = y * 2 + 16;

        draft!.values[xIndex][yIndex] += 1;
      });
    },
    [setDiscreteHeatmap]
  );

  const updateDiscreteHeatmap = useCallback(
    (oldX: number, oldY: number, newX: number, newY: number) => {
      setDiscreteHeatmap((draft) => {
        const oldXIndex = oldX * 2 + 16;
        const oldYIndex = oldY * 2 + 16;
        const newXIndex = newX * 2 + 16;
        const newYIndex = newY * 2 + 16;

        draft!.values[oldXIndex][oldYIndex] -= 1;
        draft!.values[newXIndex][newYIndex] += 1;
      });
    },
    [setDiscreteHeatmap]
  );

  const updateDiscreteHeatmapAfterPatternInsertion = useCallback(
    (newPattern: Pattern) => {
      setDiscreteHeatmap((draft) => {
        newPattern.positions.forEach((danceCouple) => {
          danceCouple.position.forEach((dancer) => {
            draft!.values[dancer.x * 2 + 16][dancer.y * 2 + 16] += 1;
          });
        });
      });
    },
    [setDiscreteHeatmap]
  );

  const updateDiscreteHeatmapAfterPatternDeletion = useCallback(
    (deletedPattern: Pattern) => {
      setDiscreteHeatmap((draft) => {
        deletedPattern.positions.forEach((danceCouple) => {
          danceCouple.position.forEach((dancer) => {
            const currentFrequency =
              draft!.values[dancer.x * 2 + 16][dancer.y * 2 + 16];
            draft!.values[dancer.x * 2 + 16][dancer.y * 2 + 16] = Math.max(
              0,
              currentFrequency - 1
            );
          });
        });
      });
    },
    [setDiscreteHeatmap]
  );

  const rotateLeft = useCallback(
    (patternId: number, brush: number[]) => {
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];

        brush.forEach((id) => {
          const coupleNumber: number = Math.floor((id - 1) / 2);
          const dancerNumberInDanceCouple: number = (id - 1) % 2;
          pattern!.positions[coupleNumber].position[
            dancerNumberInDanceCouple
          ].bodyOrientation -= 45;
          pattern!.positions[coupleNumber].position[
            dancerNumberInDanceCouple
          ].headOrientation -= 45;
        });

        activateOrientationsDisplay();
      });
    },
    [setChoreo]
  );

  const rotateRight = useCallback(
    (patternId: number, brush: number[]) => {
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];

        brush.forEach((id) => {
          const coupleNumber: number = Math.floor((id - 1) / 2);
          const dancerNumberInDanceCouple: number = (id - 1) % 2;
          pattern!.positions[coupleNumber].position[
            dancerNumberInDanceCouple
          ].bodyOrientation += 45;
          pattern!.positions[coupleNumber].position[
            dancerNumberInDanceCouple
          ].headOrientation += 45;
        });

        activateOrientationsDisplay();
      });
    },
    [setChoreo]
  );

  const rotateDancerBody = useCallback(
    (patternId: number, dancerId: number, newOrientation: number) => {
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        pattern!.positions[coupleNumber].position[
          dancerNumberInDanceCouple
        ].bodyOrientation = newOrientation;
      });
    },
    [setChoreo]
  );

  const rotateDancerHead = useCallback(
    (patternId: number, dancerId: number, newOrientation: number) => {
      setChoreo((draft) => {
        const pattern = draft?.patterns[patternId];
        const coupleNumber: number = Math.floor((dancerId - 1) / 2);
        const dancerNumberInDanceCouple: number = (dancerId - 1) % 2;
        pattern!.positions[coupleNumber].position[
          dancerNumberInDanceCouple
        ].headOrientation = newOrientation;
      });
    },
    [setChoreo]
  );

  const addShape = useCallback(
    (patternId: number, newShape: ConvexHull) => {
      setChoreo((draft) => {
        draft!.patterns[patternId].shapes.push(newShape);
      });
    },
    [setChoreo]
  );

  const removeShape = useCallback(
    (patternId: number, shapeIdToRemove: number) => {
      setChoreo((draft) => {
        draft!.patterns[patternId].shapes.splice(shapeIdToRemove, 1);
        for (let i = 0; i < draft!.patterns[patternId].shapes.length; i++) {
          draft!.patterns[patternId].shapes[i].id = i;
        }
      });
    },
    [setChoreo]
  );

  const addStandardPose = useCallback(
    (newStandardPose: Pose) => {
      setChoreo((draft) => {
        let sameNameOccurences: number = 0;
        let distinctName: string = newStandardPose.name;
        let i = 1;
        do {
          sameNameOccurences = 0;
          draft!.savedPoses.forEach((pose) => {
            if (pose.name === distinctName) sameNameOccurences++;
          });
          if (sameNameOccurences > 0) {
            distinctName = newStandardPose.name + String(i);
            i++;
          }
        } while (sameNameOccurences > 0);

        draft!.savedPoses.push({
          name: distinctName,
          limbs: newStandardPose.limbs,
        });
      });
    },
    [setChoreo]
  );

  const changeDancerPose = useCallback(
    (patternId: number, dancerId: number, newPose: Pose) => {
      setChoreo((draft) => {
        draft!.patterns[patternId].positions[
          Math.floor((dancerId - 1) / 2)
        ].position[(dancerId - 1) % 2].pose = newPose;
      });
    },
    [setChoreo]
  );

  /**
   * Changes the tangent body part of the dancer with id dancerId. The function checks if the input is valid
   * and will only act on valid inputs (e.g. a couple can not have "Left Foot" as tangent body part but "Gentleman Left Foot" or "Lady Left Foot").
   * For couples, the lady and the gentleman with both get the new tangent body part for consistency´s sake. This contradicts to the concept of
   * applying changes to selected dancer, all dancers or all ladies/gentlemen. However this is necessary, because joint couples must share the
   * same tangent body part parameter.
   */
  const changeTangentBodyPart = useCallback(
    (patternId: number, dancerId: number, newTangentBodyPart: string) => {
      setChoreo((draft) => {
        const couple =
          draft!.patterns[patternId].positions[Math.floor((dancerId - 1) / 2)];
        const dancerNumberInCouple = (dancerId - 1) % 2;
        const partnerId = dancerNumberInCouple === 0 ? 1 : 0;
        const dancer = couple.position[dancerNumberInCouple];
        const partner = couple.position[partnerId];
        const singleTangentBodyParts = [
          "Body Center",
          "Left Foot",
          "Right Foot",
        ];
        if (
          couple.joint &&
          !singleTangentBodyParts.includes(newTangentBodyPart)
        ) {
          dancer.tangentBodyPart = newTangentBodyPart;
          partner.tangentBodyPart = newTangentBodyPart;
        } else if (singleTangentBodyParts.includes(newTangentBodyPart)) {
          dancer.tangentBodyPart = newTangentBodyPart;
        }
      });
    },
    [setChoreo]
  );

  const [
    patternRotationBeforeHeatmapActivation,
    setPatternRotationBeforeHeatmapActivation,
  ] = useState(patternRotation);

  function deactivateAllModes() {
    setShowTransitions(false);
    setShowOrientations(false);
    setShowShapes(false);
    setShow3DView(false);
    setShowHeatmap(false);
    setShowTangentBodyParts(false);
  }

  function activateTangentBodyParts() {
    setShowTransitions(false);
    setShowOrientations(false);
    setShowShapes(false);
    setShow3DView(false);
    setShowHeatmap(false);

    setShowTangentBodyParts(true);

    setPatternRotationBeforeHeatmapActivation(patternRotation);
    setPatternRotation("Front");
  }

  function deactivateTangentBodyParts() {
    setPatternRotation(patternRotationBeforeHeatmapActivation);
    setShowTangentBodyParts(false);
  }

  function activateHeatmap() {
    setShowTransitions(false);
    setShowOrientations(false);
    setShowShapes(false);
    setShow3DView(false);
    setShowTangentBodyParts(false);

    setPatternRotationBeforeHeatmapActivation(patternRotation);
    setPatternRotation("Front");
    setShowHeatmap(true);
  }

  function deactivateHeatmap() {
    setPatternRotation(patternRotationBeforeHeatmapActivation);
    setShowHeatmap(false);
  }

  function activateShapes() {
    setShowTransitions(false);
    setShowOrientations(false);
    setShowHeatmap(false);
    setShow3DView(false);
    setShowTangentBodyParts(false);

    setShowShapes(true);
  }

  function deactivateShapes() {
    setShowShapes(false);
  }

  function activate3DView() {
    setShowTransitions(false);
    setShowOrientations(false);
    setShowHeatmap(false);
    setShowShapes(false);
    setShowTangentBodyParts(false);

    setShow3DView(true);
  }

  function deactivate3DView() {
    setShow3DView(false);
  }

  function toggle3DView() {
    if (show3DView) {
      deactivate3DView();
    } else {
      activate3DView();
    }
  }

  function toggleTransitionDisplay() {
    if (showTransitions) {
      deactivateTransitionDisplay();
    } else {
      activateTransitionDisplay();
    }
  }

  function activateTransitionDisplay() {
    setShowHeatmap(false);
    setShow3DView(false);
    setShowShapes(false);
    setShowOrientations(false);
    setShowTangentBodyParts(false);

    setShowTransitions(true);
  }

  function deactivateTransitionDisplay() {
    setShowTransitions(false);
  }

  function toggleOrientationsDisplay() {
    if (showOrientations) {
      deactivateOrientationsDisplay();
    } else {
      activateOrientationsDisplay();
    }
  }

  function activateOrientationsDisplay() {
    setShowHeatmap(false);
    setShow3DView(false);
    setShowShapes(false);
    setShowTransitions(false);
    setShowTangentBodyParts(false);

    setShowOrientations(true);
  }

  function deactivateOrientationsDisplay() {
    setShowOrientations(false);
  }

  function loadChoreography(newChoreo: Choreography): void {
    setChoreo(newChoreo);
    initializeDiscreteHeatmap(newChoreo);
  }

  function createNewChoreography(
    newChoreoName: string,
    newChoreoDescription: string,
    newNumCouples: number,
    firstPattern: Pattern,
    firstDance: Dance
  ) {
    setShowHeatmap(false);
    setShowShapes(false);
    setShowTransitions(false);
    setShowOrientations(false);
    setShowTangentBodyParts(false);

    setSelectedPattern(0);
    setEditingModeActive(true);
    setAnimationSpeed(1);

    setChoreoName(newChoreoName);
    setChoreoDescription(newChoreoDescription);
    
    // FIXME
    setGentlemanColor(DEFAULT_GENTLEMAN_COLOR);
    setLadyColor(DEFAULT_LADY_COLOR);
    setCoupleColor(DEFAULT_COUPLE_COLOR);

    const starterChoreo: Choreography = {
      id: 0,
      name: newChoreoName,
      description: newChoreoDescription,
      numCouples: newNumCouples,
      danceFloor: {
        width: 16,
        length: 16,
      },
      patterns: [],
      draft: [],
      dances: [firstDance],
      savedPoses: [choreo!.savedPoses[0]],
      coupleColors: coupleColors,
    };

    starterChoreo.patterns.push(firstPattern);

    setChoreo(starterChoreo);
    initializeDiscreteHeatmap(starterChoreo);
  }

  const initializeDiscreteHeatmap = useCallback((newChoreo: Choreography) => {
    let newDiscreteHeatmap: DiscreteHeatmap = {
      values: new Array(newChoreo.danceFloor.length),
    };

    for (let i = 0; i < newChoreo.danceFloor.length * 2 + 1; i++) {
      const line = new Array(newChoreo.danceFloor.width);
      newDiscreteHeatmap.values[i] = line;
      for (let j = 0; j < newChoreo.danceFloor.width * 2 + 1; j++) {
        newDiscreteHeatmap.values[i][j] = 0;
      }
    }

    let newHighestFrequency = 0;

    newChoreo.patterns.forEach((pattern) => {
      pattern.positions.forEach((danceCouple) => {
        if (danceCouple.joint) {
          const xIndex =
            Math.round(danceCouple.position[0].x * 2) + newChoreo.danceFloor.width;
          const yIndex =
            Math.round(danceCouple.position[0].y * 2) + newChoreo.danceFloor.length;
          const newValue = newDiscreteHeatmap.values[xIndex][yIndex] + 1;
          newDiscreteHeatmap.values[xIndex][yIndex] = newValue;
        } else {
          danceCouple.position.forEach((position) => {
            const xIndex = position.x * 2 + newChoreo.danceFloor.width;
            const yIndex = position.y * 2 + newChoreo.danceFloor.length;
            const newValue = newDiscreteHeatmap.values[xIndex][yIndex] + 1;
            newDiscreteHeatmap.values[xIndex][yIndex] = newValue;
            if (newValue > newHighestFrequency) {
              newHighestFrequency = newValue;
            }
          });
        }
      });
    });

    setHighestFrequency(newHighestFrequency);

    setDiscreteHeatmap(newDiscreteHeatmap);
  }, [setHighestFrequency, setDiscreteHeatmap]);

  useEffect(() => {
    // Load choreography
    async function fetchData() {
      const newChoreo = await fetchChoreography(choreoName);

      initializeDiscreteHeatmap(newChoreo);
      setChoreo(newChoreo);
    }

    fetchData();
  }, [choreoName, initializeDiscreteHeatmap, setChoreo]);

  useEffect(() => {
    if (choreo) {
      const options = choreo.patterns.map(({ id, name }: any, idx: any) => {
        return { value: idx, label: `${id} - ${name}`, key: String(id) };
      });
      setSelectOptions(options);
      setChoreoName(choreo.name);
    }
  }, [choreo]);

  const [lock, setLock] = useState<boolean>(false);
  useEffect(() => {
    const handleKeyDown = (event: { key: string }) => {
      if (event.key === "ArrowLeft") {
        previousPattern();
      }
      if (event.key === "ArrowRight") {
        console.log(selectedPattern, choreo!.patterns.length - 1);
        if (selectedPattern < choreo!.patterns.length - 1) {
          nextPattern();
        }
      }
    };

    if (choreo) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [choreo, selectedPattern]);

  useEffect(() => {
    console.log(selectedPattern);
  }, [selectedPattern]);

  const previousPattern = useCallback(() => {
    setSelectedPattern((draft) => {
      if (draft > 0) {
        return (draft -= 1);
      } else {
        return draft;
      }
    });
  }, [setSelectedPattern]);

  const nextPattern = useCallback(() => {
    setSelectedPattern((draft) => {
      console.log("Right");
      return (draft += 1);
    });
  }, [setSelectedPattern]);

  function onSelectChange(e: ChangeEvent<HTMLSelectElement>) {
    setSelectedPattern(+e.currentTarget.value);
  }

  if (!choreo) {
    return <p>Nothing to show here!</p>;
  }

  return (
    <>
      <div
        className="App"
        style={{
          minHeight: "80vh",
        }}
      >
        <NavbarComponent
          gentlemanColor={gentlemanColor}
          ladyColor={ladyColor}
          coupleColor={coupleColor}
          transitionColorScheme={transitionColorScheme}
          setGentlemanColor={setGentlemanColor}
          setLadyColor={setLadyColor}
          setCoupleColor={setCoupleColor}
          setTransitionColorScheme={setTransitionColorScheme}
          colorCodedCouples={choreo.colorCodedCouples}
          coupleColors={choreo.coupleColors}
          setCoupleColors={setCoupleColors}
          setColorCodedCouples={setColorCodedCouples}
          choreo={choreo}
          loadChoreo={loadChoreography}
          choreoName={choreo.name}
          choreoDescription={choreo.description}
          setChoreoName={setChoreoName}
          setChoreoDescription={setChoreoDescription}
          animationSpeed={animationSpeed}
          setAnimationSpeed={setAnimationSpeed}
          editingModeActive={editingModeActive}
          setEditingModeActive={setEditingModeActive}
          fineGridEnabled={fineGridEnabled}
          setFineGridEnabled={setFineGridEnabled}
          createNewChoreography={createNewChoreography}
          discreteHeatmapEnabled={discreteHeatmapEnabled}
          setDiscreteHeatmapEnabled={setDiscreteHeatmapEnabled}
          gridDragEnabled={gridDragEnabled}
          setGridDragEnabled={setGridDragEnabled}
          setPatternRotation={setPatternRotation}
          patternRotation={patternRotation}
          showHeatmap={showHeatmap}
          showGridIn3D={showGridIn3D}
          setShowGridIn3D={setShowGridIn3D}
        />
        <Container fluid>
          <Row className="mb-5">
            <VerticalToolbar
              selectValue={selectedPattern}
              onSelectChange={onSelectChange}
              selectOptions={selectOptions}
              prevDisabled={selectedPattern === 0 ? true : false}
              nextDisabled={
                selectedPattern < choreo.patterns.length - 1 ? false : true
              }
              prevClick={previousPattern}
              nextClick={nextPattern}
              selectedPattern={selectedPattern}
              setSelectedPattern={setSelectedPattern}
              toggleTransitionDisplay={toggleTransitionDisplay}
              showTransitions={showTransitions}
              choreo={choreo}
              showHeatmap={showHeatmap}
              showOrientations={showOrientations}
              toggleOrientationsDisplay={toggleOrientationsDisplay}
              showShapes={showShapes}
              activateShapes={activateShapes}
              deactivateShapes={deactivateShapes}
              activateHeatmap={activateHeatmap}
              deactivateHeatmap={deactivateHeatmap}
              showTangentBodyParts={showTangentBodyParts}
              activateTangentBodyParts={activateTangentBodyParts}
              deactivateTangentBodyParts={deactivateTangentBodyParts}
              editingModeActive={editingModeActive}
              addPattern={addPattern}
              setShowSliderForAnimatedChoreo={setShowSliderForAnimatedChoreo}
              speedForAnimatedChoreo={speedForAnimatedChoreo}
              setSpeedForAnimatedChoreo={setSpeedForAnimatedChoreo}
              show3DView={show3DView}
              toggle3DView={toggle3DView}
              setCurrentlyInAnimation={setCurrentlyInAnimation}
              animationSpeed={animationSpeed}
              highestBarNumber={highestBarNumber}
            />
            <Col xxl={5} xl={6} lg={7} className="mt-3">
              {!show3DView && (
                <DanceFloorVisualization
                  choreo={choreo}
                  selectedPattern={selectedPattern}
                  gentlemanColor={gentlemanColor}
                  ladyColor={ladyColor}
                  coupleColor={coupleColor}
                  transitionColorScheme={transitionColorScheme}
                  coupleColors={choreo.coupleColors}
                  showHeatmap={showHeatmap}
                  discreteHeatmapEnabled={discreteHeatmapEnabled}
                  discreteHeatmap={discreteHeatmap!}
                  highestFrequency={highestFrequency}
                  showTransitions={showTransitions}
                  showOrientations={showOrientations}
                  moveDancer={moveDancer}
                  rotateDancerBody={rotateDancerBody}
                  rotateDancerHead={rotateDancerHead}
                  fineGridEnabled={fineGridEnabled}
                  gridDragEnabled={gridDragEnabled}
                  editingModeActive={editingModeActive}
                  hoveredDancer={hoveredDancer}
                  setHoveredDancer={setHoveredDancer}
                  animationSpeed={animationSpeed}
                  showShapes={showShapes}
                  addShape={addShape}
                  removeShape={removeShape}
                  separateDanceCouple={separateDanceCouple}
                  joinDanceCouple={joinDanceCouple}
                  patternRotation={patternRotation}
                  currentlyInAnimation={currentlyInAnimation}
                  moveDancerInIntermediatePattern={
                    moveDancerInIntermediatePattern
                  }
                  setSelectedHeatmapTile={setSelectedHeatmapTile}
                  selectedHeatmapTile={selectedHeatmapTile}
                  addPatternToDraft={addPatternToDraft}
                  setBrushCopy={setBrushCopy}
                  setCopyOfFirstDancer={updateFirstDancerCopy}
                  showTangentBodyParts={showTangentBodyParts}
                  showGentlemanInCouple={showGentlemanInCouple}
                  changeTangentBodyPart={changeTangentBodyPart}
                />
              )}

              {show3DView && (
                <View3D
                  choreo={choreo}
                  selectedPattern={selectedPattern}
                  selectedDancerOption={selectedDancerFor3D}
                  setSelectedDancerOption={setSelectedDancerFor3D}
                  patternRotation={patternRotation}
                  show3DView={show3DView}
                  gentlemanColor={gentlemanColor}
                  ladyColor={ladyColor}
                  showGridIn3D={showGridIn3D}
                  fineGridEnabled={fineGridEnabled}
                />
              )}
            </Col>
            <Col xxl={6} xl={5} lg={4} className="mt-3">
              <p className="only-for-mobile">{`Bar: ${choreo.patterns[selectedPattern].bar}, Beat: ${choreo.patterns[selectedPattern].beat}`}</p>

              {!show3DView &&
                !showShapes &&
                !showTransitions &&
                !showHeatmap &&
                !showOrientations && (
                  <ToastContainer
                    position="top-end"
                    className="p-3"
                    style={{ zIndex: 1 }}
                  >
                    <Toast
                      onClose={() => setShowLongMovementAlert(false)}
                      show={showLongMovementAlert}
                      delay={6000}
                      bg="warning"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action might have removed some intermediate
                          patterns.
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        All intermediate patterns directly before and after the
                        pattern were deleted.
                      </Toast.Body>
                    </Toast>

                    <Toast
                      onClose={() => setShowPatternCollisionAlert(false)}
                      show={showPatternCollisionAlert}
                      delay={6000}
                      bg="danger text-white"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action is not allowed
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        You cannot move one pattern onto another!
                      </Toast.Body>
                    </Toast>

                    <Toast
                      onClose={() => setShowScalingAlert(false)}
                      show={showScalingAlert}
                      delay={6000}
                      bg="danger text-white"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action is not allowed
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        You can only scale pointsymmetric structures. The center
                        of the point symmetry is the first selected dancer with
                        a green border.
                      </Toast.Body>
                    </Toast>

                    <Toast
                      onClose={() => setShowNewPatternAlert(false)}
                      show={showNewPatternAlert}
                      delay={6000}
                      bg="danger text-white"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action is not allowed
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        Please create a new dance before inserting a new
                        pattern.
                      </Toast.Body>
                    </Toast>

                    <Toast
                      onClose={() => setShowRemoveLastDanceAlert(false)}
                      show={showRemoveLastDanceAlert}
                      delay={6000}
                      bg="danger text-white"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action is not allowed
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        You cannot delete this dance, because a choreo must have
                        at least one dance.
                      </Toast.Body>
                    </Toast>

                    <Toast
                      onClose={() => setShowRemoveLastPatternAlert(false)}
                      show={showRemoveLastPatternAlert}
                      delay={6000}
                      bg="danger text-white"
                      autohide
                    >
                      <Toast.Header>
                        <strong className="me-auto">
                          This action is not allowed
                        </strong>
                      </Toast.Header>
                      <Toast.Body>
                        You are not allowed to delete this dance, because it
                        would lead to a choreo with no pattern. A choreo must
                        have at least one pattern.{" "}
                      </Toast.Body>
                    </Toast>
                  </ToastContainer>
                )}

              <ShortcutMenu
                editingModeActive={editingModeActive}
                show3DView={show3DView}
                showShapes={showShapes}
                showTransitions={showTransitions}
                showHeatmap={showHeatmap}
                showOrientations={showOrientations}
                brushCopy={brushCopy}
                brushRotationAngle={brushRotationAngle}
                selectedPattern={selectedPattern}
                copyOfFirstDancer={copyOfFirstDancer}
                rotateLeft={rotateLeft}
                setShowOrientationModal={setShowOrientationModal}
                rotateRight={rotateRight}
                setShowFootModal={setShowFootModal}
                setBrushRotationAngle={setBrushRotationAngle}
                rotateBrush={rotateBrush}
                formScalingFactor={formScalingFactor}
                setFormScalingFactor={setFormScalingFactor}
                scaleBrush={scaleBrush}
                deactivateAllModes={deactivateAllModes}
                activateOrientations={activateOrientationsDisplay}
                activateTangentBodyParts={activateTangentBodyParts}
              />

              {showOrientations && (
                <div>
                  <Form.Label>Show specific dancers</Form.Label>
                  <Form.Check
                    type="switch"
                    id="maleFemaleSwitch"
                    label={showGentlemanInCouple ? "Gentleman" : "Ladies"}
                    className="mb-3"
                    checked={showGentlemanInCouple}
                    onChange={switchShowGentlemanInCouple}
                  />
                </div>
              )}

              {showSliderForAnimatedChoreo &&
                !(show3DView && editingModeActive) && (
                  <Form.Group className="mb-3" as={Row}>
                    <Form.Label>Time between animations in seconds</Form.Label>
                    <Col xs="11">
                      <Form.Range
                        value={speedForAnimatedChoreo}
                        onChange={(e) => {
                          setSpeedForAnimatedChoreo(+e.target.value);
                        }}
                        min={0.5}
                        max={2}
                        step={0.1}
                      />
                    </Col>
                    <Col xs="1">
                      <Form.Label>{speedForAnimatedChoreo}</Form.Label>
                    </Col>
                  </Form.Group>
                )}

              {showHeatmap && discreteHeatmapEnabled && !show3DView && (
                <Form.Group className="mb-3" as={Row}>
                  <Form.Label>Highest frequency</Form.Label>
                  <Col xs="11">
                    <Form.Range
                      value={highestFrequency}
                      onChange={(e) => setHighestFrequency(+e.target.value)}
                      min={5}
                      max={40}
                      step={1}
                    />
                  </Col>
                  <Col xs="1">
                    <Form.Label>{highestFrequency}</Form.Label>
                  </Col>
                </Form.Group>
              )}
              {!showHeatmap && !(show3DView && editingModeActive) && (
                <RemarksAccordionComponent
                  pattern={choreo.patterns[selectedPattern]}
                  remarks={choreo.patterns[selectedPattern].remarks}
                  selectedPattern={selectedPattern}
                  defaultKey="0"
                  setRemarks={setRemarks}
                  editingModeActive={editingModeActive}
                  ladyColor={ladyColor}
                  gentlemanColor={gentlemanColor}
                />
              )}

              {!(show3DView && editingModeActive) && (
                <AccordionComponent
                  title="Analysis"
                  child={
                    <DistancesBarChart
                      numCouples={choreo.numCouples}
                      pattern={choreo.patterns[selectedPattern]}
                      previousPattern={choreo.patterns[selectedPattern - 1]}
                      hasPreviousPattern={selectedPattern === 0 ? false : true}
                      gentlemanColor={gentlemanColor}
                      ladyColor={ladyColor}
                      hoveredDancer={hoveredDancer}
                      choreo={choreo}
                      showHeatmap={showHeatmap}
                      colorCodedCouples={choreo.colorCodedCouples!}
                      coupleColors={choreo.coupleColors}
                    />
                  }
                  defaultKey="1"
                />
              )}

              {!showHeatmap && !(show3DView && editingModeActive) && (
                <AccordionComponent
                  title="Positions"
                  defaultKey="1"
                  child={
                    <TableView
                      pattern={choreo.patterns[selectedPattern]}
                      colorCodedCouples={choreo.colorCodedCouples ?? false}
                      coupleColors={choreo.coupleColors}
                    />
                  }
                />
              )}

              {!show3DView && (
                <AccordionComponent
                  title="Legend"
                  child={
                    <Legend
                      showHeatmap={showHeatmap}
                      showTransitions={showTransitions}
                      showOrientations={showOrientations}
                      showShapes={showShapes}
                      showTangentBodyParts={showTangentBodyParts}
                      gentlemanColor={gentlemanColor}
                      ladyColor={ladyColor}
                      coupleColor={coupleColor}
                      colorCodedCouples={choreo.colorCodedCouples}
                      coupleColors={choreo.coupleColors}
                      highestFrequency={highestFrequency}
                      discreteHeatmapEnabled={discreteHeatmapEnabled}
                    />
                  }
                  defaultKey="1"
                />
              )}

              {show3DView && editingModeActive && (
                <PoseView
                  savedPoses={choreo.savedPoses}
                  selectedPattern={selectedPattern}
                  addStandardPose={addStandardPose}
                  changeDancerPose={changeDancerPose}
                  changeTangentBodyPart={changeTangentBodyPart}
                  selectedDancerOption={selectedDancerFor3D}
                  choreo={choreo}
                  show3DView={show3DView}
                />
              )}
            </Col>
          </Row>

          <Row className="mt-5">
            <TimelineDanceDriven
              choreo={choreo}
              selectedPattern={selectedPattern}
              setSelectedPattern={setSelectedPattern}
              setBarAndBeat={setBarAndBeat}
              gentlemanColor={gentlemanColor}
              ladyColor={ladyColor}
              coupleColor={coupleColor}
              removeBar={removeBar}
              removePattern={removePattern}
              addIntermediatePattern={addIntermediatePattern}
              editingModeActive={editingModeActive}
              timelineMapValues={timelineMapValues}
              setTimelineMapValues={setTimelineMapValues}
              highestBarNumber={highestBarNumber}
              setHighestBarNumber={setHighestBarNumber}
              addNewBar={addNewBar}
              addNewDance={addNewDance}
              setPatternName={setPatternName}
              removeAllIntermediatePatterns={removeAllIntermediatePatterns}
              updateDanceName={updateDanceName}
              removeDance={removeDance}
              promoteIntermediatePattern={promoteIntermediatePattern}
              colorCodedCouples={choreo.colorCodedCouples}
              coupleColors={choreo.coupleColors}
            />
          </Row>

          <Row>
            <BottomNavbar
              selectValue={selectedPattern}
              onSelectChange={onSelectChange}
              selectOptions={selectOptions}
              prevDisabled={selectedPattern === 0 ? true : false}
              nextDisabled={
                selectedPattern < choreo.patterns.length - 1 ? false : true
              }
              prevClick={previousPattern}
              nextClick={nextPattern}
              selectedPattern={selectedPattern}
              setSelectedPattern={setSelectedPattern}
              choreo={choreo}
              activateShapes={activateShapes}
              deactivateShapes={deactivateShapes}
              activateHeatmap={activateHeatmap}
              deactivateHeatmap={deactivateHeatmap}
              activateTangentBodyParts={activateTangentBodyParts}
              deactivateTangentBodyParts={deactivateTangentBodyParts}
              editingModeActive={editingModeActive}
              addPattern={addPattern}
              setShowSliderForAnimatedChoreo={setShowSliderForAnimatedChoreo}
              speedForAnimatedChoreo={speedForAnimatedChoreo}
              setSpeedForAnimatedChoreo={setSpeedForAnimatedChoreo}
              setCurrentlyInAnimation={setCurrentlyInAnimation}
              animationSpeed={animationSpeed}
              activateTransitions={activateTransitionDisplay}
              deactiveTransitions={deactivateTransitionDisplay}
              activate3DView={activate3DView}
              deactivate3DView={deactivate3DView}
              activateOrientations={activateOrientationsDisplay}
              deactivateOrientations={deactivateOrientationsDisplay}
              showTangentBodyParts={showTangentBodyParts}
            />
          </Row>
        </Container>

        {brushCopy.length > 0 && (
          <OrientationVisModal
            showModal={showOrientationModal}
            setShowModal={setShowOrientationModal}
            selectedPattern={selectedPattern}
            pattern={choreo.patterns[selectedPattern]}
            dancerId={
              firstDanceCouple!.joint
                ? showGentlemanInCouple
                  ? 2 * firstDanceCouple!.id - 1
                  : 2 * firstDanceCouple!.id
                : copyOfFirstDancer
            }
            frontsideColor={frontsideColorOrientationModal()}
            backsideColor={backsideColorOrientationModal()}
            rotateDancerBody={rotateDancerBody}
            rotateDancerHead={rotateDancerHead}
            editingModeActive={editingModeActive}
            brushSelectedDancers={brushCopy}
            joint={firstDanceCouple!.joint}
            pos={
              firstDanceCouple!.joint
                ? showGentlemanInCouple
                  ? firstDanceCouple!.position[0]
                  : firstDanceCouple!.position[1]
                : firstDancer
            }
            numCouples={choreo.patterns[selectedPattern].positions.length}
            showGentlemanInCouple={showGentlemanInCouple}
          />
        )}

        {brushCopy.length > 0 && (
          <FootModal
            showModal={showFootModal}
            setShowModal={setShowFootModal}
            selectedPattern={selectedPattern}
            pattern={choreo.patterns[selectedPattern]}
            dancerId={copyOfFirstDancer}
            editingModeActive={editingModeActive}
            brushSelectedDancers={brushCopy}
            joint={firstDanceCouple!.joint}
            pos={
              firstDanceCouple!.joint
                ? showGentlemanInCouple
                  ? firstDanceCouple!.position[0]
                  : firstDanceCouple!.position[1]
                : firstDancer
            }
            numCouples={choreo.patterns[selectedPattern].positions.length}
            showGentlemanInCouple={showGentlemanInCouple}
            gentlemanColor={gentlemanColor!.toString()}
            ladyColor={ladyColor!.toString()}
            coupleColor={coupleColor!.toString()}
            changeTangentBodyPart={changeTangentBodyPart}
          />
        )}

        <Modal
          show={show}
          onHide={() => setShow(false)}
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Mode change</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="center-me">
              <p>
                {`You are now entering ${
                  editingModeActive ? "editing" : "view"
                } mode`}
              </p>
              {editingModeActive && <Pencil fontSize="40" className="mb-2" />}
              {!editingModeActive && <Eye fontSize="40" className="mb-2" />}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}

export default App;
